import { useParams } from "@solidjs/router";
import BigNumber from "bignumber.js";
import type {
    ChainPairTypeTaproot,
    Pairs,
    ReversePairTypeTaproot,
    SubmarinePairTypeTaproot,
} from "boltz-swaps/client";
import { SwapType } from "boltz-swaps/types";
import { BiSolidHelpCircle } from "solid-icons/bi";
import { type Accessor, Show, createResource } from "solid-js";

import { LBTC, LN } from "../consts/Assets";
import { useCreateContext } from "../context/Create";
import { useGlobalContext } from "../context/Global";
import { calculateBoltzFeeOnSend } from "../utils/calculate";
import { formatAmount, formatDenomination } from "../utils/denomination";
import { getPair, isMobile } from "../utils/helper";
import type { ChainSwap } from "../utils/swapCreator";
import {
    unconfidentialExtra as extraFee,
    isToUnconfidentialLiquid,
} from "./Fees";
import Tooltip from "./settings/Tooltip";

const getTotalChainFees = ({
    pairs,
    sendAmount,
    assetSend,
    unconfidentialExtra,
}: {
    sendAmount: Accessor<BigNumber>;
    pairs: Accessor<Pairs | undefined>;
    assetSend: Accessor<string>;
    unconfidentialExtra: number;
}) => {
    const chainPair = getPair(
        pairs(),
        SwapType.Chain,
        assetSend(),
        LBTC,
    ) as ChainPairTypeTaproot;

    const chainMinerFees =
        chainPair.fees.minerFees.server + chainPair.fees.minerFees.user.claim;
    return calculateBoltzFeeOnSend(
        sendAmount(),
        chainPair.fees.percentage,
        chainMinerFees,
        SwapType.Chain,
    )
        .plus(chainMinerFees)
        .plus(unconfidentialExtra);
};

const getTotalSubmarineFees = ({
    pairs,
    sendAmount,
    assetSend,
}: {
    pairs: Accessor<Pairs | undefined>;
    sendAmount: Accessor<BigNumber>;
    assetSend: Accessor<string>;
}) => {
    const submarinePair = getPair(
        pairs(),
        SwapType.Submarine,
        assetSend(),
        LN,
    ) as SubmarinePairTypeTaproot;

    return calculateBoltzFeeOnSend(
        sendAmount(),
        submarinePair.fees.percentage,
        submarinePair.fees.minerFees,
        SwapType.Submarine,
    ).plus(submarinePair.fees.minerFees);
};

const getTotalReverseFees = ({
    pairs,
    sendAmount,
    unconfidentialExtra,
}: {
    pairs: Accessor<Pairs | undefined>;
    sendAmount: Accessor<BigNumber>;
    unconfidentialExtra: number;
}) => {
    const reversePair = getPair(
        pairs(),
        SwapType.Reverse,
        LN,
        LBTC,
    ) as ReversePairTypeTaproot;

    const reverseMinerFees =
        reversePair.fees.minerFees.claim + reversePair.fees.minerFees.lockup;
    return calculateBoltzFeeOnSend(
        sendAmount(),
        reversePair.fees.percentage,
        reverseMinerFees,
        SwapType.Reverse,
    )
        .plus(reverseMinerFees)
        .plus(unconfidentialExtra);
};

export const getMagicRoutingHintSavedFees = ({
    pairs,
    assetSend,
    sendAmount,
    assetReceive,
    addressValid,
    onchainAddress,
}: {
    pairs: Accessor<Pairs | undefined>;
    assetSend: Accessor<string>;
    sendAmount: Accessor<BigNumber>;
    assetReceive: Accessor<string>;
    addressValid: Accessor<boolean>;
    onchainAddress: Accessor<string>;
}) => {
    const unconfidentialExtra = isToUnconfidentialLiquid({
        assetReceive,
        addressValid,
        onchainAddress,
    })
        ? extraFee
        : 0;

    const chainFee = getTotalChainFees({
        pairs,
        sendAmount,
        assetSend,
        unconfidentialExtra,
    });
    const submarineFee = getTotalSubmarineFees({
        pairs,
        sendAmount,
        assetSend,
    });
    const reverseFee = getTotalReverseFees({
        pairs,
        sendAmount,
        unconfidentialExtra,
    });

    const savedFees = submarineFee.plus(reverseFee).minus(chainFee);

    return savedFees.toString();
};

const OptimizedRoute = () => {
    const params = useParams<{ id: string }>();

    const { t, denomination, separator, getSwap } = useGlobalContext();
    const { pair } = useCreateContext();

    const [swap] = createResource(async () => {
        if (typeof params.id !== "string") {
            return undefined;
        }

        return await getSwap<ChainSwap>(params.id);
    });

    return (
        <Show
            when={
                swap.state === "ready" &&
                swap()?.magicRoutingHintSavedFees !== undefined
            }>
            <span class="optimized-route">
                ✨{" "}
                {t("optimized_route_amount", {
                    amount: formatAmount(
                        BigNumber(swap()?.magicRoutingHintSavedFees ?? 0),
                        denomination(),
                        separator(),
                        pair().fromAsset,
                    ),
                    denomination: formatDenomination(
                        denomination(),
                        pair().fromAsset,
                    ),
                })}
                <Tooltip
                    label={{
                        key: "applied_routing_hint",
                        variables: {
                            asset: pair().toAsset,
                        },
                    }}
                    direction={[isMobile() ? "left" : "right"]}>
                    <BiSolidHelpCircle size={18} opacity={0.5} />
                </Tooltip>
            </span>
        </Show>
    );
};

export default OptimizedRoute;
